import React, { FC, useEffect, useState } from 'react'
import { AnnouncementSidebar, ImageGallery, PropertyDetails } from './sections'
import PropertyMainInfo from './sections/property-details/main-info/PropertyMainInfo'
import { ChevronNextIcon, ChevronPrevIcon, MapIcon, SearchIcon } from '@svgs'
import { Button } from 'src/components/ui'
import { useNavigate, useParams } from 'react-router-dom'
import { useGetPropertyByIdQuery } from 'src/redux/api'
import { Skeleton } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { usePageMeta } from 'src/hooks/usePageMeta'
import OwnerPanel from './sections/owner-panel/OwnerPanel'
import ListingNavigation from './sections/listing-nav/ListingNavigation'
import ErrorState from 'src/components/elements/error-state/ErrorState'
import { useSelector } from 'react-redux'
import { RootState } from 'src/redux/store'
import RelatedListings from './sections/related/RelatedListings'
import SafeDealBanner from './sections/sidebar/components/safe-deal/SafeDealBanner'
import { InfoBanner } from './sections/sidebar/components'

interface IAnnouncement {}

const typeKeyMap: Record<string, string> = {
  APARTMENT: 'apartment',
  HOUSE: 'house',
  TOWNHOUSE: 'townhouse',
  DUPLEX: 'duplex',
  PART_OF_HOUSE: 'partOfHouse',
  ROOM: 'room',
  COMMERCIAL_PROPERTY: 'commercialProperty',
  PLOT: 'plot',
  GARAGE: 'garage',
}

const Announcement: FC<IAnnouncement> = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { data: property, isLoading, isError, error, refetch } = useGetPropertyByIdQuery(id || '')
  // back where the visitor came from; a shared link opened cold has no page
  // of the site behind it, so it leads to the map
  const goBack = () => {
    const state = window.history.state as { idx?: number } | null
    if (state?.idx && state.idx > 0) navigate(-1)
    else navigate('/')
  }

  // The floating mobile CTA disappears while the real contact block is on
  // screen — otherwise it covers the very form it leads to.
  const [contactVisible, setContactVisible] = useState(false)
  const compareBarShown = useSelector(
    (state: RootState) => state.propertySearch.compareIds.length >= 2,
  )
  useEffect(() => {
    if (!property) return
    const target = document.getElementById('announcement-contact')
    if (!target || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      ([entry]) => setContactVisible(entry.isIntersecting),
      { threshold: 0.2 },
    )
    observer.observe(target)
    return () => observer.disconnect()
  }, [property])

  // SEO/browser-tab meta from the listing itself (roadmap 26.2)
  const mainPrice = property?.price?.find?.((p) => p.currency === property.currency)?.amount
  const metaTitle = property
    ? `${t(`listingType.${property.listingSection.listingType}`)} ${t(
        `step1.${typeKeyMap[property.listingSection.propertyType] || 'apartment'}`,
      )}, ${property.addressSection.settlement} — ${mainPrice} ${property.currency}`
    : undefined
  usePageMeta(metaTitle, property?.description?.slice(0, 160))

  if (isLoading) {
    // the shape of the page: the title block, the photos, the two columns
    return (
      <div className="flex flex-col gap-4 md:gap-6 md:pt-[3.75rem]">
        <Skeleton variant="rounded" height={132} className="!rounded-2xl" />
        <Skeleton variant="rounded" height={420} className="!rounded-2xl" />
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_20rem] lg:grid-cols-[minmax(0,1fr)_22.5rem] gap-4 md:gap-6">
          <Skeleton variant="rounded" height={480} className="!rounded-2xl" />
          <Skeleton variant="rounded" height={360} className="!rounded-2xl" />
        </div>
      </div>
    )
  }

  if (isError || !property) {
    // A 404 means the listing is gone or awaiting moderation — a different
    // message from a transient load failure, which invites a retry.
    const isNotFound = isError && error != null && 'status' in error && error.status === 404
    if (isNotFound) {
      // a gone listing says so and leads on, not a lone line of text
      return (
        <section
          data-testid="listing-not-found"
          className="flex flex-col items-center justify-center gap-4 min-h-[50vh] px-4 py-12 text-center"
        >
          <span className="flex items-center justify-center w-16 h-16 rounded-full bg-brand-soft">
            <span className="icon w-7 h-7 fill-brand">
              <SearchIcon />
            </span>
          </span>
          <h1 className="m-0 max-w-[28rem] text-[1.375rem] md:text-[1.75rem] font-black leading-tight text-ink">
            {t('announcement.not_found')}
          </h1>
          <div className="flex flex-wrap justify-center gap-3 mt-2">
            <Button link="/" iconAfter={<MapIcon />}>
              {t('not_found_page.to_map')}
            </Button>
            <Button variant="outlined" onClick={goBack}>
              {t('announcement.back')}
            </Button>
          </div>
        </section>
      )
    }
    return (
      <div className="flex flex-col gap-4 md:pt-[3.75rem]">
        {/* the text invites a retry, so there is a button for it */}
        <ErrorState message={t('announcement.load_error')} onRetry={refetch} />
      </div>
    )
  }

  return (
    <>
      {/* desktop: the row the header buttons float in, with a way back */}
      <div className="hidden md:flex items-center min-h-11 md:w-1/2">
        <button
          type="button"
          onClick={goBack}
          className="inline-flex items-center gap-2 h-11 px-3 -ml-3 rounded-xl text-[0.9375rem] font-bold text-ink-2 fill-current hover:text-brand-ink hover:bg-brand-soft transition-colors duration-200"
        >
          <span className="icon w-3 h-3">
            <ChevronPrevIcon />
          </span>
          {t('announcement.back')}
        </button>
      </div>
      {/* Prev/next within the feed the visitor came from (arrows, keyboard
          arrows, mobile swipe) */}
      <ListingNavigation propertyId={property.id} />
      <article className="flex flex-col gap-4 md:gap-6">
        <OwnerPanel property={property} />
        {/* the title first on a desktop, the photos first on a phone */}
        <div className="flex flex-col gap-4 md:gap-6">
          <PropertyMainInfo className="order-2 md:order-1" property={property} />
          <div className="order-1 md:order-2">
            <ImageGallery images={property.images} />
          </div>
        </div>
        {/* two columns on a desktop: the listing, and the seller who stays
            in view beside it; one column on a phone with the seller right
            after the facts */}
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_20rem] lg:grid-cols-[minmax(0,1fr)_22.5rem] gap-4 md:gap-6">
          <PropertyDetails property={property} />
          <AnnouncementSidebar
            property={property}
            className="md:col-start-2 md:row-start-1 md:row-span-2"
          />
          <div className="flex flex-col gap-4 md:gap-6 min-w-0 md:col-start-1 md:row-start-2">
            <SafeDealBanner property={property} />
            {/* the seller's other listings (owner feedback: right under the map) */}
            <RelatedListings propertyId={property.id} />
            <InfoBanner property={property} />
          </div>
        </div>
      </article>
      {/* Floating mobile shortcut that scrolls to the contact form below */}
      {!contactVisible && (
        // above the compare bar when it is shown: both sat at the same height.
        // Over the menu, or for a guest (no menu) at the bottom of the screen
        // over the home indicator; clear of the notch on a sideways iPhone.
        <div
          className={`md:hidden fixed ${compareBarShown ? 'bottom-[calc(var(--bottom-bar-h)+4.75rem)]' : 'bottom-[calc(var(--bottom-bar-h)+1.75rem)]'} inset-x-0 z-40 pl-[max(2rem,var(--safe-left))] pr-[max(2rem,var(--safe-right))]`}
        >
          <Button
            onClick={() =>
              document
                .getElementById('announcement-contact')
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
            className="!gap-0 w-full"
            classNameWrapper="flex-1"
            iconAfter={<ChevronNextIcon />}
          >
            {t('agent.contactSeller')}
          </Button>
        </div>
      )}
    </>
  )
}

export default Announcement
